import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Globe, Mail, MapPin, User, Phone, Clock, Menu, X } from 'lucide-react';

const Contact = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    pl: {
      title: 'Kontakt',
      subtitle: 'Skontaktuj się z nami w sprawie kursów IB DP Accelerator',
      contact: {
        title: 'Informacje kontaktowe',
        subtitle: 'Jesteśmy tutaj, aby pomóc Ci w przygotowaniach do egzaminów IB DP. Ważna informacja: Kursy oparte są na autorskich materiałach ekspertów'
      },
      person: {
        name: 'Mariana Nadolska',
        country: 'Poland',
        position: 'Business Projects Advisor',
        email: 'office@worldlink.pl'
      },
      location: {
        title: 'Lokalizacja kursów',
        venue: 'Siedziba WorldLINK Foundation',
        address: 'Szczegóły dotyczące lokalizacji zostaną przekazane bezpośrednio uczestnikom po potwierdzeniu rezerwacji'
      },
      hours: {
        title: 'Godziny kontaktu',
        business: 'Poniedziałek - Piątek: 9:00 - 17:00',
        weekend: 'Sobota - Niedziela: Na życzenie'
      }
    },
    en: {
      title: 'Contact',
      subtitle: 'Get in touch with us about IB DP Accelerator courses',
      contact: {
        title: 'Contact Information',
        subtitle: 'We are here to help you prepare for your IB DP exams. Important note: Courses are based on proprietary materials from our experts'
      },
      person: {
        name: 'Mariana Nadolska',
        country: 'Poland',
        position: 'Business Projects Advisor',
        email: 'office@worldlink.pl'
      },
      location: {
        title: 'Course Location',
        venue: 'WorldLINK Foundation Headquarters',
        address: 'Location details will be provided directly to participants after booking confirmation'
      },
      hours: {
        title: 'Contact Hours',
        business: 'Monday - Friday: 9:00 AM - 5:00 PM',
        weekend: 'Saturday - Sunday: By appointment'
      }
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">IB</span>
            </div>
            <span className="font-bold text-xl">DP Accelerator</span>
          </Link>
          
          <div className="hidden md:flex space-x-6">
            <Link to="/" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Strona główna' : 'Home'}</Link>
            <Link to="/experts" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Eksperci' : 'Experts'}</Link>
            <Link to="/schedule" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Harmonogram' : 'Schedule'}</Link>
            <Link to="/course-topics" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Tematy' : 'Topics'}</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}</Link>
            <Link to="/code-of-conduct" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Regulamin' : 'Code of Conduct'}</Link>
            <Link to="/contact" className="text-blue-600 font-semibold">{language === 'pl' ? 'Kontakt' : 'Contact'}</Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              className="flex items-center space-x-1"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'pl' ? 'EN' : 'PL'}</span>
            </Button>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to="/" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Strona główna' : 'Home'}
              </Link>
              <Link 
                to="/experts" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Eksperci' : 'Experts'}
              </Link>
              <Link 
                to="/schedule" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Harmonogram' : 'Schedule'}
              </Link>
              <Link 
                to="/course-topics" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Tematy' : 'Topics'}
              </Link>
              <Link 
                to="/pricing" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
              </Link>
              <Link 
                to="/code-of-conduct" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
              </Link>
              <Link 
                to="/contact" 
                className="block text-blue-600 font-semibold"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Kontakt' : 'Contact'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4 text-gray-900">{t.contact.title}</h2>
              <p className="text-xl text-gray-600">{t.contact.subtitle}</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Main Contact Person */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">{t.person.name}</CardTitle>
                      <CardDescription className="text-lg">{t.person.position}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-gray-600" />
                    <span className="font-medium">{t.person.country}</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <a 
                      href={`mailto:${t.person.email}`}
                      className="font-medium text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      {t.person.email}
                    </a>
                  </div>
                </CardContent>
              </Card>
              
              {/* Location */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">{t.location.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-semibold text-gray-900">{t.location.venue}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">{t.location.address}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Contact Hours */}
            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <CardTitle className="text-xl text-amber-900">{t.hours.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-medium text-amber-900">{t.hours.business}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="font-medium text-amber-900">{t.hours.weekend}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Quick Contact CTA */}
            <div className="text-center mt-12">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">
                    {language === 'pl' ? 'Gotowy na rozmowę?' : 'Ready to talk?'}
                  </h3>
                  <p className="text-blue-800 mb-6">
                    {language === 'pl' 
                      ? 'Skontaktuj się z nami już dziś, aby dowiedzieć się więcej o kursach IB DP Accelerator.'
                      : 'Contact us today to learn more about IB DP Accelerator courses.'}
                  </p>
                  <a 
                    href={`mailto:${t.person.email}?subject=IB DP Accelerator - ${language === 'pl' ? 'Zapytanie' : 'Inquiry'}`}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    {language === 'pl' ? 'Wyślij email' : 'Send Email'}
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Link to="/" className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">IB</span>
              </div>
              <span className="font-bold text-xl">DP Accelerator</span>
            </Link>
            <p className="text-gray-400">
              {language === 'pl' 
                ? 'Intensywne kursy przygotowawcze do matury międzynarodowej IB.' 
                : 'Intensive preparation courses for the International Baccalaureate.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;